import React from 'react';
import PropTypes from 'prop-types';
import { Link, graphql } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
} from 'react-share';
import Seo from '@laradevitt/gatsby-theme-just-basics/src/components/seo';
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews';

import { LogoMonogramWhite } from '../components/Brand';
import {
  Stack,
  Switcher
} from '../components/EveryLayout';
import { Section } from '../components/Layout';
import { FadeInOpacity } from '../components/Misc/FadeIn';
import {
  Badge,
  SocialShareWrapper,
  SocialShareEmail,
  SocialShareFacebook,
  SocialShareLinkedIn,
  SocialShareTwitter
} from '../components/Page';
import {
  Blockquote,
  InfoBoxes
} from '../components/Page/Slices';

/* eslint-disable react/prop-types */
export const Head = ({ data }) => {

  const { prismicStory } = data;

  let seoImageUrl = prismicStory.data.featured_image?.thumbnails?.OpenGraph?.url || prismicStory.data.featured_image?.url || '';
  if (prismicStory.data.main_image?.url) {
    seoImageUrl = prismicStory.data.main_image?.thumbnails?.OpenGraph?.url || prismicStory.data.main_image?.url;
  }

  return (
    <Seo
      title={prismicStory.data.title.text}
      description={prismicStory.data.teaser?.text || ''}
      image={seoImageUrl}
      path={`/impact/meet-our-clients/${prismicStory.uid}/`}
    />
  );
};
/* eslint-enable react/prop-types */

const Story = ({
  data,
}) => {
  const { site, prismicStory } = data;

  let mainImage = null;
  if (prismicStory.data.main_image?.gatsbyImageData) {
    mainImage = prismicStory.data.main_image;
    if (prismicStory.data.main_image.thumbnails?.Portrait?.gatsbyImageData) {
      mainImage = prismicStory.data.main_image.thumbnails.Portrait;
    }
  }
  else {
    if (prismicStory.data.featured_image?.url) {
      mainImage = prismicStory.data.featured_image;
    }
  }

  return (
    <div className="bg-off-white pb-16 2xl:pb-32">
      <Section className="bg-off-white py-4">
        <div className="max-w-8xl">
          <FadeInOpacity className="flex flex-wrap justify-between pb-12">
            <div className="mx-auto lg:grow lg:mx-0">
              {mainImage
              ? (
                <figure className="max-w-[800px] lg:max-w-[572px]">
                  <div className="mx-auto lg:aspect-w-3 lg:aspect-h-4">
                    <img src={mainImage.url} alt={mainImage.alt ? mainImage.alt : ''} className="object-cover max-h-[600px] lg:max-h-[none]" />
                  </div>
                  {/* 16px > 21px, lh 20px > 25px */}
                  {/* No captions yet!
                  <figcaption className="sm:text-[1.3rem] mt-3">
                    Lorem ipsum dolor sit amet, consectetur adipiscing elit. In consequat arcu augue, eget sodales purus consectetur et. Nullam eget erat eros.
                  </figcaption>
                  */}
                </figure>
              ) : (
                <div className="max-w-[800px] lg:max-w-[572px]">
                  <div className="bg-nordic-blue-500 px-12 py-10 2xl:px-24 2xl:py-20">
                    <div className="mx-auto lg:aspect-w-3 lg:aspect-h-4">
                      <LogoMonogramWhite className="max-w-[8rem] mx-auto flex items-center" />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="lg:w-[60%] xl:w-[53%] flex items-center">
              <Stack className="pt-4 lg:pl-4 2xl:pl-0 mb-16">
                <Link
                  className="inline-block pr-2 font-trade text-[1rem] sm:text-xs 2xl:text-[1.3rem] leading-[1.4] 2xl:leading-[1.2]"
                  to="/impact/meet-our-clients/"
                >
                  Client Stories
                </Link>
                {/* 36px > 88px, lh 42px > 92px */}
                <h1 className="text-xl 2xl:text-[5.5rem] 2xl:leading-[1.05] 2xl:mb-10">{prismicStory.data.title.text}</h1>
                {/* 28px > 40px, lh 34px > 46px */}
                {prismicStory.data.teaser && (
                  <div className="text-[1.75rem] sm:text-lg 2xl:text-[2.5rem] leading-[1.215] sm:leading-[var(--base-line-height)] 2xl:leading-[1.15] mt-8" dangerouslySetInnerHTML={{ __html: prismicStory.data.teaser.html }} />
                )}
              </Stack>
            </div>
          </FadeInOpacity>
          <div>
            <SocialShareWrapper>
              <LinkedinShareButton
                title={`Abler Nordic Client Story: ${prismicStory.data.title.text}`}
                summary={prismicStory.data.teaser?.text || ''}
                source={`Shared from the ${site?.siteMetadata?.title} website`}
                url={`${site?.siteMetadata?.siteUrl}/impact/meet-our-clients/${prismicStory.uid}/`}
              >
                <SocialShareLinkedIn className="rounded-sm h-11 w-11" aria-hidden="true" />
              </LinkedinShareButton>
              <FacebookShareButton
                url={`${site?.siteMetadata?.siteUrl}/impact/meet-our-clients/${prismicStory.uid}/`}
                hashtag={`#AblerNordic`}
              >
                <SocialShareFacebook className="rounded-sm h-11 w-11" aria-hidden="true" />
              </FacebookShareButton>
              <TwitterShareButton
                url={`${site?.siteMetadata?.siteUrl}/impact/meet-our-clients/${prismicStory.uid}/`}
                title={`Abler Nordic Client Story: ${prismicStory.data.title.text}`}
                via={`Abler_Nordic`}
              >
                <SocialShareTwitter className="rounded-sm h-11 w-11" aria-hidden="true" />
              </TwitterShareButton>
              <EmailShareButton
                subject={`Abler Nordic Client Story: ${prismicStory.data.title.text}`}
                url={`${site?.siteMetadata?.siteUrl}/impact/meet-our-clients/${prismicStory.uid}/`}
                body={`Shared from the ${site?.siteMetadata?.title} website:`}
              >
                <SocialShareEmail className="rounded-sm h-11 w-11" aria-hidden="true" />
              </EmailShareButton>
            </SocialShareWrapper>
          </div>
        </div>
      </Section>
      <Section className="bg-off-white pt-4 mb-16">
        {prismicStory.data.body?.html
          && (
          <FadeInOpacity className="max-w-8xl pb-12">
            <div className="flex flex-wrap">
              <div className="xl:grow" />
              <div className="xl:w-[53%]">
                <div
                  className="prose prose-custom"
                  dangerouslySetInnerHTML={{ __html: prismicStory.data.body.html }}
                />
              </div>
            </div>
          </FadeInOpacity>
        )}
        {/* begin Slices */}
        {prismicStory.data?.body1 && prismicStory.data.body1.map((slice, i) => {

          // Safeguard against slice types that we aren't querying for.
          if (!slice.id) return null;

          // Convert id to class.
          const id = `slice-${slice.id.toString().substring(0,5)}-${i}`;

          // For image___text slice; can't declare it inside of case clause.
          let imageAlignClass = 'image-left';

          return (
            <FadeInOpacity
              key={slice.id.toString()}
              id={id}
              className={`slice-${slice.slice_type}`}
            >
              {(() => {
                switch (slice.slice_type) {
                  case 'rich_text':
                    return (
                      <div className="max-w-8xl mb-16">
                        <div className="flex flex-wrap">
                          <div className="xl:grow" />
                          <div className="xl:w-[53%]">
                            <div
                              id={id}
                              className="prose prose-custom"
                              dangerouslySetInnerHTML={{ __html: slice.primary.text.html }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  case 'blockquote':
                    return (
                      <div className="max-w-8xl mx-auto mb-16">
                        <Blockquote slice={slice} colour="nordic-blue-500" />
                      </div>
                    );
                  case 'image_grid':
                    return (
                      <div className="mx-auto max-w-8xl mb-16 flex">
                        <div className={`image-grid image-grid-${slice.items.length} inline-grid items-start gap-4 my-6 2xl:my-16 mx-auto`}>
                          {slice.items.map((item, j) => {
                            return (
                              <React.Fragment key={`${item.id}-grid-${j}`}>
                                {item.caption?.html ? (
                                  <figure className="max-w-[800px] mx-auto">
                                    <GatsbyImage
                                      image={item.image.gatsbyImageData}
                                      alt={item.image?.alt || ''}
                                      objectFit="contain"
                                    />
                                    {caption
                                      && (
                                      <figcaption
                                        className="italic xl:mx-4"
                                        dangerouslySetInnerHTML={{ __html: item.caption.html }}
                                      />
                                    )}
                                  </figure>
                                ): (
                                  <div className="max-w-[800px] mx-auto">
                                    <GatsbyImage
                                      image={item.image.gatsbyImageData}
                                      alt={item.image?.alt || ''}
                                      objectFit="contain"
                                    />
                                  </div>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      </div>
                    );
                  case 'image___text':
                    return (
                      <div className="mx-auto max-w-8xl flex mb-16">
                        <Switcher className="gap-12">
                          <div className={`${ slice.primary.alignment === 'Align image left' ? ' md:order-2 sm:order-none' : '' }`}>
                            <div
                              className="prose prose-custom"
                              dangerouslySetInnerHTML={{ __html: slice.primary.text.html }}
                            />
                          </div>
                          <div className={`flex${ slice.primary.alignment === 'Align image left' ? ' md:order-1 sm:order-none' : '' }`}>
                            <figure className="max-w-[800px] mx-auto">
                              <GatsbyImage
                                image={slice.primary.image.gatsbyImageData}
                                alt=""
                                objectFit="contain"
                              />
                            </figure>
                          </div>
                        </Switcher>
                      </div>
                    );

                  case 'info_boxes':
                    return (
                      <div className="mx-auto max-w-8xl flex justify-center mb-16">
                        <InfoBoxes slice={slice} colour="nordic-blue-500" />
                      </div>
                    );

                  default:
                    return '';
                }
              })()}
            </FadeInOpacity>
          )
        })}
        {/* end Slices */}
      </Section>
    </div>
  );
};

Story.propTypes = {
  data: PropTypes.shape({
    prismicStory: PropTypes.shape({
      uid: PropTypes.string,
      data: PropTypes.shape({
        body1: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            slice_type: PropTypes.string,
            primary: PropTypes.shape({
              alignment: PropTypes.string,
              image: PropTypes.shape({
                gatsbyImageData: PropTypes.object,
                alt: PropTypes.string,
              }),
              text: PropTypes.shape({
                html: PropTypes.string,
              }),
            }),
            items: PropTypes.arrayOf(
              PropTypes.shape({
                image: PropTypes.shape({
                  gatsbyImageData: PropTypes.object,
                  alt: PropTypes.string,
                }),
              }),
            ),
          }),
        ),
      }),
    }),
  }),
};

export default withPrismicPreview(Story);

export const pageQuery = graphql`
  query ($uid: String!) {
    site {
      siteMetadata {
        title
        description
        author
        siteUrl
      }
    }
    prismicStory(
      uid: {
        eq: $uid
      }
    ) {
      _previewable
      uid
      data {
        date(formatString: "MMMM D, YYYY")
        title {
          text
        }
        body {
          html
        }
        teaser {
          html
          text
        }
        main_image {
          url
          alt
          gatsbyImageData(
            width: 1200
          )
          thumbnails {
            OpenGraph {
              alt
              url(
                imgixParams: {
                  q: 100
                }
              )
            }
            Portrait {
              alt
              url
              gatsbyImageData(
                width: 900
              )
            }
          }
        }
        featured_image {
          url (
            imgixParams: {
              rect: "0,0,0,0",
              h: 1.0,
              w: 800
            }
          )
          alt
        }
        body1 {
          __typename
          ...on PrismicStoryDataBody1RichText {
            id
            slice_type
            primary {
              text {
                html
              }
            }
          }
          ...on PrismicStoryDataBody1Blockquote {
            id
            slice_type
            primary {
              text {
                html
              }
            }
          }
          ...on PrismicStoryDataBody1ImageText {
            id
            slice_type
            primary {
              image {
                url
                alt
                gatsbyImageData(
                  width: 960
                  imgixParams: {
                    rect: "0,0,0,0",
                  }
                )
              }
              text {
                html
              }
              alignment
            }
          }
          ...on PrismicStoryDataBody1ImageGrid {
            id
            slice_type
            items {
              image {
                url
                alt
                gatsbyImageData(
                  width: 960
                  imgixParams: {
                    rect: "0,0,0,0",
                  }
                )
              }
            }
          }
          ...on PrismicStoryDataBody1InfoBoxes {
            id
            slice_type
            items {
              datapoint
              summary {
                html
              }
              footnote {
                html
              }
            }
            primary {
              image {
                alt
                thumbnails {
                  Landscape {
                    gatsbyImageData(
                      width: 800
                    )
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
